import styled from 'styled-components';

import { Loader } from 'components/Loader';
import { ActionSection } from './ActionSection';
import { ColumnDetailsSection } from './ColumnDetailsSection';
import { GeneralInfoSection } from './GeneralInfoSection';
import { OtherSiteSetupSection } from './OtherSiteSetupSection';
import { PhotosSection } from './PhotosSection';
import { TicketSection } from './TicketSection';
import { TimingSection } from './TimingSection';

import {
    SiteSetupFragmentFragment,
    useGetMicrostoreProductsQuery,
    useGetSetupSiteInventoryQuery,
    useGetSiteSetupDeliveryQuery,
    TotadminPermission,
} from 'data/__generated__';
import { toast } from 'react-toastify';
import { useHasPermission } from 'hooks/useHasPermission';

export const SiteSetupDetailsContent = ({ siteSetup }: { siteSetup: SiteSetupFragmentFragment }) => {
    // L'inventaire est une ressource à part (`siteInventories`) : sans le droit, le détail des colonnes est
    // verrouillé (`ColumnDetailsSection`) et la requête ne part pas — sinon « Accès refusé » ferait tomber
    // toute la page.
    const canReadSiteInventories = useHasPermission(TotadminPermission.SiteInventoriesRead);
    const {
        data: siteInventoryData,
        loading: siteInventoryLoading,
        error: siteInventoryError,
    } = useGetSetupSiteInventoryQuery({
        variables: { siteSetupId: siteSetup._id },
        skip: !canReadSiteInventories,
    });

    const {
        data: getMicrostoreProductsData,
        loading: getMicrostoreProductsLoading,
        error: getMicrostoreProductsError,
    } = useGetMicrostoreProductsQuery();

    // Resolve the rangement's OWN delivery (fixed or dynamic) via its `delivery` field, so a dynamic
    // rangement shows its own delivery — not the fixed one (see GET_SITE_SETUP_DELIVERY_QUERY).
    const {
        data: getDeliveryData,
        loading: getDeliveryLoading,
        error: getDeliveryError,
    } = useGetSiteSetupDeliveryQuery({ variables: { siteSetupId: siteSetup._id } });

    if (getDeliveryLoading || siteInventoryLoading || getMicrostoreProductsLoading) {
        return (
            <Content>
                <Loader />
            </Content>
        );
    }

    if (
        getDeliveryError ||
        !getDeliveryData ||
        siteInventoryError ||
        (canReadSiteInventories && !siteInventoryData) ||
        getMicrostoreProductsError ||
        !getMicrostoreProductsData
    ) {
        throw new Error(
            `L'erreur suivante est survenue : ${getDeliveryError || siteInventoryError || getMicrostoreProductsError}`,
        );
    }

    const siteInventory = siteInventoryData?.getSetupSiteInventory ?? null;
    const { getMicrostoreProducts: products } = getMicrostoreProductsData;
    const delivery = getDeliveryData.getSiteSetup?.delivery ?? null;

    if (!delivery) {
        toast.warning(`Il n'y a pas de livraison prévu pour ce rangement à la date ${siteSetup.date}`);
    }

    const deliveredStoreProductQuantities = (delivery?.boxes ?? [])
        .filter(({ columnId }) => columnId)
        .reduce<Record<string, number>>((acc, box) => {
            box.products.forEach((product) => {
                const { _id: productId, quantity, quantityPicked } = product;
                acc[productId] = (acc[productId] ?? 0) + (quantityPicked ?? quantity);
            });

            return acc;
        }, {});

    return (
        <Content>
            <ScrollableContent>
                <GeneralInfoSection siteSetup={siteSetup} />
                <ActionSection siteSetup={siteSetup} />
                <OtherSiteSetupSection siteSetup={siteSetup} />
                <TimingSection siteSetup={siteSetup} />
                <ColumnDetailsSection
                    deliveredStoreProductQuantities={deliveredStoreProductQuantities}
                    deliveryProductSummary={delivery?.stockTransfersProductSummary ?? []}
                    products={products}
                    siteSetup={siteSetup}
                    siteInventory={siteInventory}
                />
                <PhotosSection siteSetup={siteSetup} />
                <TicketSection siteSetup={siteSetup} />
            </ScrollableContent>
        </Content>
    );
};

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
